import React, { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { SearchIcon, MessageCircleIcon, SparklesIcon, RotateCcwIcon } from 'lucide-react';
import { Section } from '../../ui/Section';
import { Button, ButtonAnchor } from '../../ui/Button';
import { ChipGroup } from './ChipGroup';
import { CompareCard } from './CompareCard';
import { ComparisonTable } from './ComparisonTable';
import { useVehicles } from '../../../contexts/VehiclesContext';
import { whatsappLink } from '../../../utils/vehicles';
import { ANIO_DESDE_OPTIONS, ANIO_HASTA_OPTIONS, COMBUSTIBLE_OPTIONS, KM_OPTIONS, PREF_OPTIONS, PRICE_RANGES, SELECTOR_WA_MESSAGE, TIPO_OPTIONS, TRANSMISION_OPTIONS, initialSelector, selectorCatalogQuery, selectorResults, type SelectorState } from '../../../utils/selector';
function StepHeader({
  step,
  title,
  text
}: {
  step: number;
  title: string;
  text: string;
}) {
  return <div data-id="element-441" data-mp="25tds78qs0t60">
      <h3 className="flex items-center gap-2.5 text-lg font-bold text-white" data-id="element-442" data-mp="25tm530mwfyh7">
        <span className="grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-neon/15 text-[13px] font-black text-neon" data-id="element-443" data-mp="1g3g26sj0pd6d">
          {step}
        </span>
        {title}
      </h3>
      <p className="mt-2 text-[13.5px] leading-relaxed text-mist" data-id="element-444" data-mp="16d3i11f47m0r">{text}</p>
    </div>;
}
export function SelectorUsados() {
  const {
    vehicles
  } = useVehicles();
  const [state, setState] = useState<SelectorState>(initialSelector);
  const resultsRef = useRef<HTMLDivElement>(null);
  const results = useMemo(() => selectorResults(vehicles, state), [vehicles, state]);
  const top = results.slice(0, 3);
  const set = (patch: Partial<SelectorState>) => setState(prev => ({
    ...prev,
    ...patch
  }));
  const toggleInList = (key: 'tipos' | 'prefs', id: string) => setState(prev => ({
    ...prev,
    [key]: prev[key].includes(id) ? prev[key].filter(x => x !== id) : [...prev[key], id]
  }));
  const isDirty = JSON.stringify(state) !== JSON.stringify(initialSelector);
  const reveal = () => {
    resultsRef.current?.scrollIntoView({
      block: 'start',
      behavior: 'smooth'
    });
  };
  return <Section id="selector" className="bg-surface" data-id="element-445" data-mp="2g90a7qvkl2pl">
      <div className="mb-10 max-w-3xl md:mb-12" data-id="element-446" data-mp="2baoz7ctjgm5s">
        <p className="inline-flex items-center gap-2 rounded-full border border-neon/40 bg-neon/10 px-3 py-1.5 text-[12.5px] font-semibold text-neon-soft" data-id="element-447" data-mp="2bbgvkpuymnx4">
          <SparklesIcon className="h-4 w-4" aria-hidden="true" data-id="element-448" data-mp="3405oi1lv3mxs" />
          Selector inteligente VCARS
        </p>
        <h2 className="mt-5 text-3xl font-extrabold leading-[1.08] tracking-tight text-white md:text-[42px]" data-id="element-449" data-mp="2z1ug7vb7tkho">
          Elegí tu usado seleccionado ideal
        </h2>
        <p className="mt-4 text-base leading-relaxed text-mist md:text-lg" data-id="element-450" data-mp="2pb9iea3d8jzt">
          Filtrá por rango, preferencias, año y kilometraje. Te mostramos las opciones de
          nuestro catálogo que mejor se adaptan a vos.
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,380px)_minmax(0,1fr)] lg:gap-8" data-id="element-451" data-mp="3hy4234sczfc8">
        {/* Pasos 1 y 2 */}
        <div className="space-y-4" data-id="element-452" data-mp="3qa4fpg2c3yzo">
          <div className="rounded-2xl border border-line bg-surface2/40 p-6" data-id="element-453" data-mp="3u9mvjxfatjvn">
            <StepHeader step={1} title="Elegí tu rango y preferencias" text="Definí qué tipo de usado seleccionado estás buscando y cuál es tu rango de inversión." data-id="element-454" data-mp="3ubpvpaa60te3" />

            <div className="mt-6 space-y-6" data-id="element-455" data-mp="d21d5wa0e7lm">
              <div data-id="element-456" data-mp="d19nt370963s">
                <label htmlFor="selector-q" className="mb-2.5 block text-[12px] font-semibold uppercase tracking-wide text-mist" data-id="element-457" data-mp="czos3a8yfh4c">
                  Búsqueda por texto
                </label>
                <div className="flex items-center gap-2.5 rounded-xl border border-line bg-surface2/70 px-3.5 transition-colors duration-150 ease-smooth focus-within:border-neon" data-id="element-458" data-mp="qtftfvcj7uoz">
                  <SearchIcon className="h-4 w-4 shrink-0 text-neon" aria-hidden="true" data-id="element-459" data-mp="10r40nkrbquo1" />
                  <input id="selector-q" type="search" value={state.q} onChange={e => set({
                  q: e.target.value
                })} placeholder="Buscá por marca, modelo o versión" className="h-11 w-full bg-transparent text-sm text-white placeholder:text-mist/70 focus:outline-none" data-id="element-460" data-mp="1xiqzwlsx0ogh" />
                </div>
              </div>

              <ChipGroup label="Rango de precio" options={PRICE_RANGES.map(r => ({
              id: r.id,
              label: r.label
            }))} selected={state.precio ? [state.precio] : []} onSelect={id => set({
              precio: state.precio === id ? '' : id
            })} data-id="element-461" data-mp="1eko6zvx8y7de" />

              <ChipGroup label="Tipo de vehículo" multiple options={TIPO_OPTIONS.map(t => ({
              id: t.id,
              label: t.id
            }))} selected={state.tipos} onSelect={id => toggleInList('tipos', id)} data-id="element-462" data-mp="1jhelietekuwp" />

              <ChipGroup label="Preferencias" multiple options={PREF_OPTIONS.map(p => ({
              id: p,
              label: p
            }))} selected={state.prefs} onSelect={id => toggleInList('prefs', id)} data-id="element-463" data-mp="32gumajbzbd5k" />
            </div>
          </div>

          <div className="rounded-2xl border border-line bg-surface2/40 p-6" data-id="element-464" data-mp="2xj2vjsn2lx2y">
            <StepHeader step={2} title="Año y kilometraje" text="Elegí el año mínimo y el kilometraje máximo que preferís para tu próximo usado." data-id="element-465" data-mp="3l96n31mzi6dl" />

            <div className="mt-6 space-y-6" data-id="element-466" data-mp="3bhaprssq2kaf">
              <ChipGroup label="Año desde" options={ANIO_DESDE_OPTIONS.map(a => ({
              id: a,
              label: a
            }))} selected={state.anioDesde ? [state.anioDesde] : []} onSelect={id => set({
              anioDesde: state.anioDesde === id ? '' : id
            })} data-id="element-467" data-mp="3bghov8gg2p07" />
              <ChipGroup label="Año hasta" options={ANIO_HASTA_OPTIONS.map(a => ({
              id: a,
              label: a
            }))} selected={state.anioHasta ? [state.anioHasta] : []} onSelect={id => set({
              anioHasta: state.anioHasta === id ? '' : id
            })} data-id="element-468" data-mp="mobzmtr394gs" />
              <ChipGroup label="Kilometraje máximo" options={KM_OPTIONS} selected={[state.kmMax]} onSelect={id => set({
              kmMax: id
            })} data-id="element-469" data-mp="8ot3z0fgrlld" />
              <ChipGroup label="Combustible" options={COMBUSTIBLE_OPTIONS.map(c => ({
              id: c,
              label: c
            }))} selected={state.combustible ? [state.combustible] : []} onSelect={id => set({
              combustible: state.combustible === id ? '' : id
            })} data-id="element-470" data-mp="3v3vprjwgq6rm" />
              <ChipGroup label="Transmisión" options={TRANSMISION_OPTIONS.map(t => ({
              id: t,
              label: t
            }))} selected={state.transmision ? [state.transmision] : []} onSelect={id => set({
              transmision: state.transmision === id ? '' : id
            })} data-id="element-471" data-mp="3h6flof1n2p31" />
            </div>

            <div className="mt-7 flex flex-wrap gap-2.5" data-id="element-472" data-mp="21lws39tbg92d">
              <Button size="lg" onClick={reveal} data-id="element-473" data-mp="25nibzf49pnm2">
                Encontrar mi usado ideal
              </Button>
              {isDirty ? <Button variant="outline" size="lg" onClick={() => setState(initialSelector)} data-id="element-474" data-mp="18yapo4vtrah3">
                  <RotateCcwIcon className="h-4 w-4" aria-hidden="true" data-id="element-475" data-mp="1hx5sr83ko4g6" />
                  Reiniciar
                </Button> : null}
            </div>
          </div>
        </div>

        {/* Paso 3 — comparativa */}
        <div ref={resultsRef} className="scroll-mt-24" data-id="element-476" data-mp="q5q8vltegvtn">
          <div className="rounded-2xl border border-line bg-surface2/25 p-6 md:p-8" data-id="element-477" data-mp="u7bh2tuzolxi">
            <StepHeader step={3} title="Compará las mejores opciones" text="Según los filtros elegidos, te mostramos unidades disponibles de nuestro catálogo para que compares precio, año, kilometraje, financiación y permuta." data-id="element-478" data-mp="3jpn5nktiaf70" />

            {top.length > 0 ? <>
                <p className="mt-6 text-sm text-mist" data-id="element-479" data-mp="3oo6txrvko32j">
                  <span className="font-bold text-white" data-id="element-480" data-mp="6jdbbkrfzg32">{results.length}</span>{' '}
                  {results.length === 1 ? 'unidad coincide' : 'unidades coinciden'}{' '}
                  con tu búsqueda · mostramos las {top.length} mejores coincidencias
                </p>

                <div className="mt-5 grid gap-5 sm:grid-cols-2 xl:grid-cols-3" data-id="element-481" data-mp="b9k0pmts34md">
                  {top.map((v, i) => <CompareCard key={v.id} vehicle={v} rank={i} data-id="element-482" data-mp="pao0nxhirksu" data-mpi={i} />)}
                </div>

                <ComparisonTable vehicles={top} data-id="element-483" data-mp="l9ucr442u5zy" />

                <div className="mt-6 flex flex-wrap items-center gap-3" data-id="element-484" data-mp="13wtfwbynexi1">
                  {results.length > 3 ? <Link to={selectorCatalogQuery(state)} className="inline-flex h-11 items-center justify-center rounded-xl bg-neon px-5 text-sm font-semibold text-white transition-colors duration-150 ease-smooth hover:bg-neon-soft" data-id="element-485" data-mp="18vwm3zqd79rl">
                      Ver más unidades del catálogo
                    </Link> : <Link to={selectorCatalogQuery(state)} className="inline-flex h-11 items-center justify-center rounded-xl border border-line bg-surface2/70 px-5 text-sm font-semibold text-white transition-colors duration-150 ease-smooth hover:border-neon/70" data-id="element-486" data-mp="px23ugrozr6o">
                      Ver el catálogo filtrado
                    </Link>}
                  <ButtonAnchor href={whatsappLink(SELECTOR_WA_MESSAGE)} variant="whatsapp" size="md" data-id="element-487" data-mp="74zor9up8iyj">
                    <MessageCircleIcon className="h-4 w-4" aria-hidden="true" data-id="element-488" data-mp="lrcy86zcvauk" />
                    Quiero que un asesor me ayude
                  </ButtonAnchor>
                </div>
              </> : <div className="mt-6 rounded-2xl border border-line bg-surface2/50 p-7 text-center" data-id="element-489" data-mp="cq5dkrpri8iy">
                <p className="text-lg font-semibold text-white" data-id="element-490" data-mp="10fpyvh9e1hve">
                  No encontramos una unidad exacta con esos filtros, pero podemos
                  ayudarte a conseguirla.
                </p>
                <p className="mt-2 text-sm text-mist" data-id="element-491" data-mp="qp4qx37tr0zt">
                  Si no encontrás lo que buscás, un asesor VCARS puede ayudarte a
                  conseguirlo.
                </p>
                <div className="mt-6 flex flex-wrap justify-center gap-2.5" data-id="element-492" data-mp="1jchdvehwdatn">
                  <ButtonAnchor href={whatsappLink(SELECTOR_WA_MESSAGE)} variant="whatsapp" size="lg" data-id="element-493" data-mp="1o5u1blaubbkt">
                    <MessageCircleIcon className="h-4 w-4" aria-hidden="true" data-id="element-494" data-mp="33wmjwqy85322" />
                    Quiero que un asesor me ayude
                  </ButtonAnchor>
                  <Button variant="outline" size="lg" onClick={() => setState(initialSelector)} data-id="element-495" data-mp="3hpltydf8m528">
                    Reiniciar filtros
                  </Button>
                </div>
              </div>}

            <p className="mt-6 text-[12.5px] leading-relaxed text-mist" data-id="element-496" data-mp="2kzbl17gi8bn5">
              Todas las unidades mostradas están sujetas a disponibilidad.
            </p>
          </div>
        </div>
      </div>
    </Section>;
}